import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const FLUSH_INTERVAL = 30000; // 30 seconds
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let eventQueue = [];
let pageEnteredAt = null;
let currentPage = null;

function flushEvents(token) {
  if (!eventQueue.length || !token) return;
  const batch = [...eventQueue];
  eventQueue = [];
  fetch(`${BACKEND_URL}/api/activity/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ events: batch }),
  }).catch(() => {
    // Re-queue on failure
    eventQueue.push(...batch);
  });
}

function flushBeacon(userId) {
  if (!eventQueue.length || !userId) return;
  const batch = [...eventQueue];
  eventQueue = [];
  const blob = new Blob(
    [JSON.stringify({ user_id: userId, events: batch })],
    { type: 'application/json' }
  );
  navigator.sendBeacon(`${BACKEND_URL}/api/activity/track-beacon`, blob);
}

export function trackEvent(type, action, metadata = {}) {
  eventQueue.push({
    type,
    action,
    page: window.location.pathname,
    metadata,
    timestamp: new Date().toISOString(),
  });
}

export function useActivityTracker() {
  const location = useLocation();
  const { user, token } = useAuth();
  const timerRef = useRef(null);

  const flush = useCallback(() => {
    if (token) flushEvents(token);
  }, [token]);

  // Track page views
  useEffect(() => {
    if (!user) return;

    // Record duration on previous page
    if (currentPage && pageEnteredAt) {
      const duration = Math.round((Date.now() - pageEnteredAt) / 1000);
      if (duration > 0 && duration < 3600) {
        eventQueue.push({
          type: 'page_duration',
          page: currentPage,
          duration_seconds: duration,
          timestamp: new Date().toISOString(),
        });
      }
    }

    currentPage = location.pathname;
    pageEnteredAt = Date.now();

    eventQueue.push({
      type: 'page_view',
      page: location.pathname,
      timestamp: new Date().toISOString(),
    });
  }, [location.pathname, user]);

  // Periodic flush
  useEffect(() => {
    if (!token) return;
    timerRef.current = setInterval(() => flushEvents(token), FLUSH_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [token]);

  // Flush on unload
  useEffect(() => {
    const handleUnload = () => {
      if (currentPage && pageEnteredAt) {
        const duration = Math.round((Date.now() - pageEnteredAt) / 1000);
        if (duration > 0 && duration < 3600) {
          eventQueue.push({
            type: 'page_duration',
            page: currentPage,
            duration_seconds: duration,
            timestamp: new Date().toISOString(),
          });
        }
      }
      if (user?.id) flushBeacon(user.id);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user]);

  return { trackEvent, flush };
}
